import React from 'react'
import { IconName, makeIcon } from './icons'

export interface SegmentSpec {
  icon?: IconName
  text: string
  onSelect: () => void
}

export interface SegmentedButtonProps {
  allowedSelection: 'single' | 'multi'
  segments: SegmentSpec[]
  selectedIdx: number | number[] | null
  className?: string
  ariaLabel?: string
}

const isSelectedSegment = (
  idx: number,
  selectedIdx: SegmentedButtonProps['selectedIdx'],
): boolean => {
  if (selectedIdx === null) return false
  if (Array.isArray(selectedIdx)) return selectedIdx.includes(idx)
  return selectedIdx === idx
}

export const SegmentedButton: React.FC<SegmentedButtonProps> = ({
  allowedSelection,
  segments,
  selectedIdx,
  className = '',
  ariaLabel,
}) => {
  const isSingle = allowedSelection === 'single'
  const classes = ['segmented-button', className].filter(Boolean).join(' ')

  return (
    <div
      className={classes}
      role={isSingle ? 'radiogroup' : 'group'}
      aria-label={ariaLabel}
    >
      {segments.map((segment, idx) => {
        const selected = isSelectedSegment(idx, selectedIdx)
        const segmentClasses = [
          'segmented-button__segment',
          selected ? 'segmented-button__segment--selected' : '',
        ].filter(Boolean).join(' ')

        const ariaProps = isSingle
          ? { role: 'radio', 'aria-checked': selected }
          : { 'aria-pressed': selected }

        return (
          <button
            // eslint-disable-next-line react/no-array-index-key
            key={idx}
            type="button"
            className={segmentClasses}
            onClick={() => segment.onSelect()}
            {...ariaProps}
          >
            {segment.icon && (
              <span className="segmented-button__icon" aria-hidden="true">
                {makeIcon(segment.icon, '16px')}
              </span>
            )}
            <span className="segmented-button__text">{segment.text}</span>
          </button>
        )
      })}
    </div>
  )
}
